import { computed, ref } from 'vue'
import {
  RND_KEY,
  SESSION,
  UIN,
} from '@repo/common-utils/constants/user'
import { SKILLHUB_LEAD_FLAG, SKILLHUB_LEAD_VALUE } from '@repo/common-utils/constants/skillhubLeadAuth'
import {
  hasSkillhubLeadAuth,
  hasUcSession,
  hasUinStored,
} from '@repo/common-utils/utils/ucSessionAndLeadStorage'
import { logoutUc } from '@/services/authApi'
import {
  clearSkillhubAuthSnapshot,
  tryRestoreAfterExternalH5Navigation,
} from '@/utils/skillhubSessionBackup'

const epoch = ref(0)

const WATCHED_KEYS = [SESSION, UIN, RND_KEY, SKILLHUB_LEAD_FLAG, SKILLHUB_LEAD_VALUE] as const

function isLoggedInStorage(): boolean {
  return hasUcSession() || hasSkillhubLeadAuth()
}

/** 登录态写入后调用，使用同一 composable 的组件会更新 */
export function notifySessionAuthChanged(): void {
  epoch.value++
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === null || (WATCHED_KEYS as readonly string[]).includes(e.key)) {
      tryRestoreAfterExternalH5Navigation()
      notifySessionAuthChanged()
    }
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    if (tryRestoreAfterExternalH5Navigation()) {
      notifySessionAuthChanged()
    }
  })
}

export function useSessionAuth() {
  const isLoggedIn = computed(() => {
    void epoch.value
    return isLoggedInStorage()
  })

  /** 仅当存在 uin（真 UC 会话或 lead 分支下发的 uin）时为 true；随 epoch 响应式。
   *  apiKey 接口以 X-uin 鉴权，有 uin 即可拉取，故不再要求完整 UC 会话三件套。 */
  const hasUin = computed(() => {
    void epoch.value
    return hasUinStored()
  })

  /** 纯数字 uin，用于埋点上报；无有效 uin 时为 undefined（不参与上报）。 */
  const uin = computed<number | undefined>(() => {
    void epoch.value
    if (!hasUinStored()) return undefined
    let raw = ''
    try {
      raw = localStorage.getItem(UIN) || ''
    } catch {
      return undefined
    }
    const n = Number(raw)
    return Number.isFinite(n) && n !== 0 ? n : undefined
  })

  /** 用于头像角标等，随 epoch 与登录态同步；引流态为手机号 e164 字符串 */
  const uinLabel = computed(() => {
    void epoch.value
    try {
      if (hasUcSession()) {
        return localStorage.getItem(UIN) || ''
      }
      if (hasSkillhubLeadAuth()) {
        return localStorage.getItem(SKILLHUB_LEAD_VALUE) || ''
      }
      return ''
    } catch {
      return ''
    }
  })

  const avatarLetter = computed(() => {
    const u = uinLabel.value
    if (!u) return '?'
    const digits = u.replace(/\D/g, '')
    const last = digits ? digits.slice(-1) : u.slice(-1)
    return /^\d$/.test(last) ? last : u.slice(0, 1).toUpperCase()
  })

  async function logout(): Promise<void> {
    clearSkillhubAuthSnapshot()
    await logoutUc()
    notifySessionAuthChanged()
  }

  return { isLoggedIn, hasUin, uin, logout, notifySessionAuthChanged, uinLabel, avatarLetter }
}
